import { useQuery } from '@tanstack/react-query';
import Head from 'next/head';
import { useCallback, useMemo, useState } from 'react';
import NftDetailsLayout from '@/layouts/NftDetailsLayout';
import { useNftContent } from '@/lib/providers/NftContextProvider';
import { useContent } from '@/provider/ContentProvider';
import { useMenuDetails } from '@/provider/MenuContext';
import { truncateWithEllipsis } from '@/utils/truncateString.utils';
import RenderIf from '@/Component/RenderIf';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import withAuth from '@/lib/components/AuthSessionValidate';
import { GetServerSideProps } from 'next';
import { NFTDetails } from '@/Component/NFTCard';

type Props = {
  nftDetails: NFTDetails;
  contract: string;
  token: string;
};

// -------- getServerSideProps --------
export const getServerSideProps: GetServerSideProps = async (context) => {
  const { contract, token } = context.params!;

  try {
    // For server-side rendering, we need to construct the full URL
    const protocol = process.env.NODE_ENV === 'production' ? 'https' : 'http';
    const host = context.req.headers.host || 'localhost:3000';
    const baseUrl = `${protocol}://${host}`;

    // Fetch NFT data using getByTokenId API
    const response = await fetch(`${baseUrl}/api/getByTokenId?tokenId=${token}`);

    if (!response.ok) {
      return { notFound: true };
    }

    const nftDetails = await response.json();

    // Verify the contract address matches
    if (nftDetails.contract_Address !== contract) {
      return { notFound: true };
    }

    return {
      props: { nftDetails, contract, token }
    };
  } catch (err) {
    console.error('Error fetching NFT data:', err);
    return { notFound: true };
  }
};

const NftDetailPage: React.FC<Props> = ({ contract, token, nftDetails }) => {
  const theme = useTheme();
  const router = useRouter();

  const [balanceTokenQuantity, setBalanceTokenQuantity] = useState<number>(0);
  const { content } = useContent();
  const { setMenuIndex } = useMenuDetails();

  const {
    isNftListLoading,
    selectedNft,
    contactGalleryItems,
    nftFilter: { isSaleStarted, isSaleEnded, endDate },
    fetchBuyersList,
    fetchSoldItemsCount,
    refetchSoldTokens,
    setSelectedNft
  } = useNftContent();

  const { data: buyersList, isLoading: buyerIsLoading } = useQuery({
    queryKey: ['buyer', token],
    queryFn: async () => await fetchBuyersList(token!),
    enabled: !!token,
    gcTime: 0
  });

  const { isLoading: isSoldTokenLoading } = useQuery({
    queryKey: ['sold_items_count'],
    queryFn: async () => {
      await refetchSoldTokens();

      return null;
    }
  });

  const { isLoading } = useQuery({
    queryKey: ['nftDetails', { contract, token, id: nftDetails?.token_id }],
    queryFn: async () => {
      if (!nftDetails?.token_id) return null;

      const isSoldToken = await fetchSoldItemsCount(token);

      if (!nftDetails?.token_id || !contract || !token) return;
      setMenuIndex(5);

      // ERC721 sold or not check without quantity
      if (nftDetails?.token_type == 'ERC721' && isSoldToken?.length !== 0) {
        setBalanceTokenQuantity(nftDetails?.quantity || 0);
        setSelectedNft({
          ...nftDetails,
          isSoldOut: true,
          owner: nftDetails?.owner
        });

        return nftDetails || null;
      }

      // ERC1155 sold or not check with quantity
      if (nftDetails?.token_type == 'ERC1155' && isSoldToken?.length !== 0) {
        setSelectedNft({
          ...nftDetails,
          isSoldOut: nftDetails?.quantity === isSoldToken?.[0]?.count,
          owner: nftDetails?.owner
        });
        setBalanceTokenQuantity((nftDetails?.quantity || 0) - (isSoldToken?.[0]?.count || 0));

        return nftDetails || null;
      }

      // BuyNow without check
      setSelectedNft({
        ...nftDetails,
        isSoldOut: false,
        owner: nftDetails?.owner
      });
      setBalanceTokenQuantity((nftDetails?.quantity || 0) - (isSoldToken?.[0]?.count || 0));

      return nftDetails || null;
    },
    enabled: !!contract && !!token && !!nftDetails?.token_id && !isSoldTokenLoading
  });

  const isContactGallery = useMemo(() => {
    if (contactGalleryItems?.length > 0) {
      const isTrue = contactGalleryItems.find(
        (item) => item.tokenId === nftDetails?.token_id && item.saleId === nftDetails?.saleId
      );

      return isTrue?.saleId ? true : false;
    }

    return false;
  }, [contactGalleryItems, nftDetails?.token_id, nftDetails?.saleId]);

  const onBackToHome = useCallback(() => {
    router.push('/');
  }, [router]);

  return (
    <>
      <RenderIf condition={!!nftDetails?.token_id}>
        <Head>
          <title>{`${nftDetails?.title}: ${content.title}`}</title>
          <meta name="description" content={truncateWithEllipsis(nftDetails?.description, 155)} />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Head>
      </RenderIf>
      <RenderIf condition={isLoading || isSoldTokenLoading || isNftListLoading || !!nftDetails?.token_id}>
        <NftDetailsLayout
          isSaleStarted={isSaleStarted}
          isSaleEnded={isSaleEnded}
          selectedNft={selectedNft || { ...nftDetails, isSoldOut: false }}
          endDate={endDate}
          loading={isLoading || buyerIsLoading || isSoldTokenLoading || isNftListLoading}
          balanceTokenQuantity={balanceTokenQuantity}
          isContactGallery={isContactGallery}
          buyersList={buyersList}
        />

        <RenderIf.Else>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px',
              flexDirection: 'column',
              minHeight: 'calc(100vh - 180px)'
            }}
          >
            <Typography
              sx={{
                color: theme?.palette?.text?.primary,
                fontSize: '32px'
              }}
            >
              404 Page not found
            </Typography>
            <Button onClick={onBackToHome}>Back to home page</Button>
          </Box>
        </RenderIf.Else>
      </RenderIf>
    </>
  );
};

export default withAuth(NftDetailPage);
